import React, { useState, useRef, useEffect, RefObject } from "react";
import AwesomeIcon from "../../AwesomeIcon";
import {
  faPaperPlaneTop,
  faMicrophone,
  faUserTag,
  faWaveformLines,
  faLightbulb,
  faTimes,
  faStop,
  faSearch,
  faMemoPad,
  faStar,
  faFileLines,
  faArrowsRotate,
  faLayerGroup,
} from "@fortawesome/pro-regular-svg-icons";
import classNames from "classnames";
import { useStoreState } from "pullstate";
import Store from "../../../utils/Store";
import constants from "../../../utils/constants";
import { int } from "@kitware/vtk.js/types";
import { isEmpty, truncate, get } from "lodash";
import ComingSoonPopup from "../ComingSoonPopup";
import promptsData from "../PromptsModal/prompts.json";
import { trackEvent } from "../../../utils/mixpanel";
import { MIXPANEL_EVENTS } from "../../../utils/mixpanelEvents";

interface ChatInputProps {
  onSend: (value: string) => void;
  disabled?: boolean;
  inProgress?: boolean;
  isVisible?: boolean;
  placeholder?: string;
}

const placeholders = [
  "Type `/` to see all available actions and prompts.",
  "Click the mic and Speak: control the assistant handsfree.",
  "Try `/add patient` to quickly add a new patient.",
  "Type `/list forms` to see and fill available forms.",
];

interface Prompt {
  name: string;
  content: string;
  section: string;
  icon?: string;
}

interface PromptsJson {
  sections: Array<{
    title: string;
    icon?: string;
    prompts: Array<{ name: string; content: string }>;
  }>;
}

const flattenPrompts = (data: PromptsJson): Prompt[] => {
  return data.sections.flatMap((section) =>
    section.prompts.map((prompt) => ({
      name: prompt.name,
      content: prompt.content,
      section: section.title,
      icon: section.icon,
    }))
  );
};

const allPrompts = flattenPrompts(promptsData as PromptsJson);

// Icon mapping for prompt section headers
const sectionIconMap: Record<string, any> = {
  faUserTag,
  faSearch,
  faMemoPad,
  faStar,
  faFileLines,
  faArrowsRotate,
  faLayerGroup,
};

const ChatInput: React.FC<ChatInputProps> = ({
  onSend,
  disabled = false,
  inProgress = false,
}) => {
  const [inputValue, setInputValue] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [isFocused, setIsFocused] = useState(false);
  const [currentPlaceholder, setCurrentPlaceholder] = useState(0);
  const [isPlaceholderVisible, setIsPlaceholderVisible] = useState(true);
  const [showComingSoonPopup, setShowComingSoonPopup] = useState<"voice" | null>(null);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const recognitionRef = useRef<any>(null);
  const transcribeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [interimTranscript, setInterimTranscript] = useState("");
  const [showPromptDropdown, setShowPromptDropdown] = useState(false);
  const [promptQuery, setPromptQuery] = useState("");
  const [filteredPrompts, setFilteredPrompts] = useState<Prompt[]>(allPrompts);
  const [selectedPromptIndex, setSelectedPromptIndex] = useState<number>(0);
  const textareaContainerRef = useRef<HTMLDivElement>(null);
  const promptItemRefs = useRef<Array<HTMLDivElement | null>>([]);
  const [selection, setSelection] = useState<{ start: number; end: number } | null>(null);
  const baseInputValueRef = useRef("");
  const baseSelectionRef = useRef<{ start: number; end: number } | null>(null);
  // Track prompt modal open (slash) only on first open per session
  const promptDropdownOpenedRef = useRef(false);

  // Get selected patient from Store
  const { patientDetail } = useStoreState(Store, (s) => ({
    patientDetail: s.patientDetail,
  }));

  const { chatInputContent, isDraggableModalOpen } = useStoreState(Store, (s) => ({
    chatInputContent: s.chatInputContent,
    isDraggableModalOpen: s.isDraggableModalOpen,
  }));

  // Rotate placeholders every 8 seconds with fade animation
  useEffect(() => {
    let interval: NodeJS.Timeout;
    let timeout: NodeJS.Timeout;

    const startRotation = () => {
      interval = setInterval(() => {
        setIsPlaceholderVisible(false);
        timeout = setTimeout(() => {
          setCurrentPlaceholder((prev) => (prev + 1) % placeholders.length);
          setIsPlaceholderVisible(true);
        }, 500);
      }, 6000);
    };

    const stopRotation = () => {
      if (interval) {
        clearInterval(interval);
      }
      if (timeout) {
        clearTimeout(timeout);
      }
      setIsPlaceholderVisible(true);
    };

    if (!inputValue.trim()) {
      startRotation();
    } else {
      stopRotation();
    }

    return () => {
      stopRotation();
    };
  }, [inputValue]); // Add inputValue as dependency to react to changes

  // Add clipboard monitoring
  useEffect(() => {
    if (isDraggableModalOpen && chatInputContent) {
      setInputValue(chatInputContent);
      setTimeout(() => {
        Store.update((s) => {
          s.chatInputContent = "";
        });
      }, 900);
    }
  }, [isDraggableModalOpen]);

  useEffect(() => {
    if (!inProgress) {
      textareaRef.current?.focus();
    }
  }, [inProgress]);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${Math.min(
        textareaRef.current.scrollHeight,
        200
      )}px`;
    }
  }, [inputValue, isDraggableModalOpen]);

  // Reset textarea height when modal is minimized
  useEffect(() => {
    if (!isDraggableModalOpen && textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = "60px"; // Set a default height when minimized
    }
  }, [isDraggableModalOpen]);

  // Clean up recognition and timer on unmount
  useEffect(() => {
    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.onresult = null;
        recognitionRef.current.onerror = null;
        recognitionRef.current.onend = null;
        recognitionRef.current.stop();
      }
      if (transcribeTimeoutRef.current) {
        clearTimeout(transcribeTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const handleShortcut = (e: KeyboardEvent) => {
      // CMD+K (Mac) or CTRL+K (Windows/Linux)
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        // Only trigger if input is visible (optionally: isDraggableModalOpen)
        if (!disabled && !inProgress) {
          handleMicClick();
        }
      }
    };
    window.addEventListener("keydown", handleShortcut);
    return () => window.removeEventListener("keydown", handleShortcut);
  }, [disabled, inProgress, isTranscribing]);

  // Detect slash command and update dropdown
  useEffect(() => {
    const match = inputValue.match(/(^|\s)\/(\w*)$/);
    if (match) {
      setShowPromptDropdown(true);
      setPromptQuery(match[2] || "");
      const filtered = allPrompts.filter((p) =>
        p.name.toLowerCase().includes((match[2] || "").toLowerCase())
      );
      setFilteredPrompts(filtered);
      setSelectedPromptIndex(0);
    } else {
      setShowPromptDropdown(false);
      setPromptQuery("");
      setFilteredPrompts(allPrompts);
      setSelectedPromptIndex(0);
    }
  }, [inputValue]);

  // Scroll selected prompt into view when selectedPromptIndex changes
  useEffect(() => {
    if (showPromptDropdown && filteredPrompts.length > 0) {
      const ref = promptItemRefs.current[selectedPromptIndex];
      if (ref) {
        ref.scrollIntoView({ block: "nearest" });
      }
    }
  }, [selectedPromptIndex, showPromptDropdown, filteredPrompts]);

  // Track prompt modal open (slash) only on first open per session
  useEffect(() => {
    if (showPromptDropdown && !promptDropdownOpenedRef.current) {
      trackEvent(MIXPANEL_EVENTS.ASSISTANT_PROMPT_MODAL_OPEN, { method: "slash" });
      promptDropdownOpenedRef.current = true;
    }
    if (!showPromptDropdown) {
      promptDropdownOpenedRef.current = false;
    }
  }, [showPromptDropdown]);

  const handleSubmit = (e?: React.FormEvent) => {
    e?.preventDefault();
    // Stop recording if active
    if (isTranscribing) {
      stopRecognition();
    }
    if (inputValue.trim() && !disabled && !inProgress) {
      // Track send via button
      if (e && e.type === "click") {
        trackEvent(MIXPANEL_EVENTS.ASSISTANT_CHATINPUT_SEND_BUTTON);
      }
      onSend(inputValue);
      setInputValue("");
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      // Track send via enter
      trackEvent(MIXPANEL_EVENTS.ASSISTANT_CHATINPUT_SEND_ENTER);
      handleSubmit();
    }
  };

  const getGender = (gender: int) => {
    const genderObj = constants.GENDERS.find((g) => g.value === gender);
    return genderObj?.shortLabel;
  };

  const onClickOpenPromptsModal = () => {
    const dummyAnchor = document.createElement("a");
    dummyAnchor.href = "#prompts";
    dummyAnchor.click();
    Store.update((s) => {
      s.isDraggableModalOpen = !s.isDraggableModalOpen;
    });
  };

  const getPatientStatus = () => {
    const aiInference = get(patientDetail, ["latest_study", "ai_inference"]);
    if (aiInference === constants.AI_INFERENCE.POSITIVE) {
      return {
        isAbnormal: true,
        label: "Abnormal",
      };
    } else if (aiInference === constants.AI_INFERENCE.NEGATIVE) {
      return {
        isAbnormal: false,
        label: "Normal",
      };
    }
    return null;
  };

  const patientStatus = getPatientStatus();

  // Handlers for accept/cancel
  const stopRecognition = () => {
    recognitionRef.current?.stop();
    setIsTranscribing(false);
    if (transcribeTimeoutRef.current) {
      clearTimeout(transcribeTimeoutRef.current);
    }
    // Track stop recording
    trackEvent(MIXPANEL_EVENTS.ASSISTANT_CHATINPUT_STOP_RECORDING);
  };

  // Update selection on textarea events
  const handleSelect = (e: React.SyntheticEvent<HTMLTextAreaElement>) => {
    const sel = {
      start: e.currentTarget.selectionStart,
      end: e.currentTarget.selectionEnd,
    };
    setSelection(sel);
    baseSelectionRef.current = sel;
  };

  // Insert or replace text at selection
  const insertAtSelection = (
    text: string,
    baseValue: string,
    selection: { start: number; end: number } | null
  ) => {
    if (!selection) return baseValue + text;
    const { start, end } = selection;
    return baseValue.slice(0, start) + text + baseValue.slice(end);
  };

  // Handle speech recognition results
  const handleSpeechResult = (final: string, interim: string) => {
    // Always apply to baseInputValueRef/baseSelectionRef
    if (final) {
      const baseValue = baseInputValueRef.current;
      const sel = baseSelectionRef.current || {
        start: baseValue.length,
        end: baseValue.length,
      };
      const newValue = insertAtSelection(final, baseValue, sel);
      setInputValue(newValue);
      baseInputValueRef.current = newValue;
      // Move caret after inserted text
      setTimeout(() => {
        if (textareaRef.current) {
          const newPos = sel.start + final.length;
          textareaRef.current.focus();
          textareaRef.current.setSelectionRange(newPos, newPos);
          setSelection({ start: newPos, end: newPos });
          baseSelectionRef.current = { start: newPos, end: newPos };
        }
      }, 0);
      setInterimTranscript("");
    } else {
      setInterimTranscript(interim);
    }
  };

  const handleMicClick = () => {
    if (isTranscribing) {
      stopRecognition();
      return;
    }
    // Track speak button
    trackEvent(MIXPANEL_EVENTS.ASSISTANT_CHATINPUT_SPEAK);
    // Set baseInputValue and baseSelection to current state when starting transcription
    if (textareaRef.current) {
      baseInputValueRef.current = inputValue;
      baseSelectionRef.current = {
        start: textareaRef.current.selectionStart,
        end: textareaRef.current.selectionEnd,
      };
    } else {
      baseInputValueRef.current = inputValue;
      baseSelectionRef.current = selection || {
        start: inputValue.length,
        end: inputValue.length,
      };
    }
    const SpeechRecognition =
      (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SpeechRecognition) {
      alert("Speech recognition is not supported in this browser.");
      return;
    }
    const recognition = new SpeechRecognition();
    recognition.lang = "en-US";
    recognition.interimResults = true;
    recognition.continuous = true;
    recognition.onresult = (event: any) => {
      let interim = "";
      let final = "";
      for (let i = event.resultIndex; i < event.results.length; ++i) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          final += transcript;
        } else {
          interim += transcript;
        }
      }
      handleSpeechResult(final, interim);
    };
    recognition.onerror = (event: any) => {
      setIsTranscribing(false);
      if (transcribeTimeoutRef.current) {
        clearTimeout(transcribeTimeoutRef.current);
      }
    };
    recognition.onend = () => {
      setIsTranscribing(false);
      if (transcribeTimeoutRef.current) {
        clearTimeout(transcribeTimeoutRef.current);
      }
    };
    recognitionRef.current = recognition;
    setIsTranscribing(true);
    setInterimTranscript("");
    recognition.start();
  };

  // Keyboard navigation for prompt dropdown
  const handlePromptKeyDown = (e: KeyboardEvent) => {
    if (!showPromptDropdown || filteredPrompts.length === 0) return;
    e.stopPropagation();
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedPromptIndex((i) => (i + 1) % filteredPrompts.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedPromptIndex(
        (i) => (i - 1 + filteredPrompts.length) % filteredPrompts.length
      );
    } else if (e.key === "Tab" || e.key === "Enter") {
      e.preventDefault();
      if (filteredPrompts[selectedPromptIndex]) {
        selectPrompt(filteredPrompts[selectedPromptIndex]);
      }
    } else if (e.key === "Escape") {
      setShowPromptDropdown(false);
    }
  };

  // Insert prompt content into input and place cursor at end
  const selectPrompt = (prompt: Prompt) => {
    // Track prompt selected
    trackEvent(MIXPANEL_EVENTS.ASSISTANT_PROMPT_SELECTED, { promptName: prompt.name });
    setInputValue((prev) => {
      const newValue = prev.replace(/(^|\s)\/(\w*)$/, `$1${prompt.content}`);
      setTimeout(() => {
        if (textareaRef.current) {
          textareaRef.current.focus();
          textareaRef.current.setSelectionRange(newValue.length, newValue.length);
        }
      }, 0);
      return newValue;
    });
    setShowPromptDropdown(false);
    setPromptQuery("");
    setFilteredPrompts(allPrompts);
    setSelectedPromptIndex(0);
  };

  // Attach keydown handler to textarea
  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    const handler = (e: KeyboardEvent) => handlePromptKeyDown(e);
    textarea.addEventListener("keydown", handler);
    return () => textarea.removeEventListener("keydown", handler);
  }, [showPromptDropdown, filteredPrompts, selectedPromptIndex]);

  return (
    <form onSubmit={handleSubmit} className="relative w-full">
      <div className="flex flex-col w-full p-4 border-t border-gray-800 bg-transparent">
        <div
          ref={textareaContainerRef}
          className={classNames(
            "relative w-full transition-all duration-200 border rounded-2xl",
            isTranscribing
              ? "border-[1.5px] border-[#FF7869]/60 animate-[chatinput-red-border-pulse_1s_infinite]"
              : isFocused
              ? "border-teal-500 shadow-[0_3px_10px_rgba(30,40,50,0.3)]"
              : "border-gray-700 shadow-[0_3px_8px_rgba(20,25,35,0.25)]"
          )}
        >
          <div
            className={classNames(
              "flex items-center px-4 py-3 dark:bg-[#232830] bg-gray-600 rounded-t-2xl border-b border-gray-700",
              { hidden: isEmpty(patientDetail?.patient_id) }
            )}
          >
            <div className="relative">
              <div className="p-3 rounded-full bg-teal-100/20 dark:bg-teal-900 flex items-center justify-center">
                <AwesomeIcon name={faUserTag} size="lg" className="text-teal-200" />
              </div>
            </div>
            <div className="font-bold break-words break-all text-white mx-2">
              {truncate(patientDetail.name, { length: 20 })}
            </div>
            <div className="text-gray-300 text-md mr-3">
              {patientDetail.age}Y <span>{getGender(patientDetail.gender)}</span>
            </div>
            <div className="text-gray-300 mr-2">{patientDetail.id}</div>

            {/* Status Pill */}
            {patientStatus && (
              <div className="px-4 py-2 bg-gray-900 rounded-full font-medium text-gray-200 flex items-center">
                <div
                  className={classNames(
                    "h-3 w-3 rounded-full mr-2",
                    patientStatus.isAbnormal ? "bg-red-500" : "bg-green-500"
                  )}
                ></div>
                <span className="text-md">{patientStatus.label}</span>
              </div>
            )}
          </div>

          {/* Prompts dropdown absolutely positioned above textarea */}
          {showPromptDropdown && filteredPrompts.length > 0 && (
            <div
              className="absolute left-0 bottom-[70%] mb-0 z-50 min-w-[250px] max-w-[350px] w-10/12 scrollbar-light dark:scrollbar"
              style={{ pointerEvents: "auto" }}
            >
              <div className="bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-lg shadow-2xl max-h-64 overflow-y-auto">
                {/* Group prompts by section and render sticky headers */}
                {(() => {
                  const sectionMap: Record<string, { icon?: string; prompts: Prompt[] }> =
                    {};
                  filteredPrompts.forEach((prompt) => {
                    if (!sectionMap[prompt.section]) {
                      sectionMap[prompt.section] = { icon: prompt.icon, prompts: [] };
                    }
                    sectionMap[prompt.section].prompts.push(prompt);
                  });
                  const orderedSections = (promptsData as PromptsJson).sections
                    .map((section) => section.title)
                    .filter((title) => sectionMap[title]);
                  return orderedSections.map((sectionTitle, sectionIdx) => (
                    <React.Fragment key={sectionTitle}>
                      {/* Sticky section header */}
                      <div className="sticky top-0 z-10 flex items-center gap-2 px-4 pt-3 pb-1 bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 font-semibold text-gray-700 dark:text-gray-200 text-sm">
                        {sectionMap[sectionTitle].icon && (
                          <AwesomeIcon
                            name={sectionIconMap[sectionMap[sectionTitle].icon!]}
                            className="w-4 h-4 text-brand-peach"
                          />
                        )}
                        <span>{sectionTitle}</span>
                      </div>
                      {/* Prompts for this section */}
                      {sectionMap[sectionTitle].prompts.map((prompt, idx) => {
                        const globalIdx = filteredPrompts.findIndex(
                          (p) => p.name === prompt.name && p.section === prompt.section
                        );
                        return (
                          <div
                            key={prompt.name + idx}
                            ref={(el) => (promptItemRefs.current[globalIdx] = el)}
                            className={`flex items-center px-4 py-2 cursor-pointer transition-colors duration-150 border-l-4 border-transparent
                              ${
                                globalIdx === selectedPromptIndex
                                  ? "bg-teal-100 dark:bg-teal-900 border-teal-400/80"
                                  : "hover:bg-teal-50 dark:hover:bg-teal-900"
                              }
                              `}
                            onMouseDown={(e: React.MouseEvent<HTMLDivElement>) => {
                              e.preventDefault();
                              selectPrompt(prompt);
                            }}
                            onMouseEnter={() => setSelectedPromptIndex(globalIdx)}
                          >
                            <span className="font-semibold text-gray-800 dark:text-gray-100">
                              {prompt.name}
                            </span>
                          </div>
                        );
                      })}
                    </React.Fragment>
                  ));
                })()}
                {filteredPrompts.length === 0 && (
                  <div className="px-4 py-2 text-gray-400">No prompts found</div>
                )}
              </div>
            </div>
          )}
          <div
            className={classNames(
              "relative px-4 py-2 dark:bg-[#2b333d] bg-gray-200",
              isEmpty(patientDetail?.patient_id) && "rounded-t-2xl"
            )}
          >
            <div className="relative">
              <textarea
                ref={textareaRef}
                value={(() => {
                  if (isTranscribing && interimTranscript) {
                    // Show interim transcript at selection, but do not update inputValue
                    const baseValue = baseInputValueRef.current;
                    const sel = baseSelectionRef.current || {
                      start: baseValue.length,
                      end: baseValue.length,
                    };
                    return insertAtSelection(interimTranscript, baseValue, sel);
                  }
                  return inputValue;
                })()}
                onChange={(e) => {
                  setInputValue(e.target.value);
                  baseInputValueRef.current = e.target.value;
                  const sel = {
                    start: e.target.selectionStart,
                    end: e.target.selectionEnd,
                  };
                  setSelection(sel);
                  baseSelectionRef.current = sel;
                }}
                onKeyDown={handleKeyDown}
                onFocus={() => setIsFocused(true)}
                onBlur={() => setIsFocused(false)}
                onSelect={handleSelect}
                placeholder={placeholders[currentPlaceholder]}
                disabled={inProgress}
                className={classNames(
                  "w-full px-4 py-3 pr-6 bg-transparent text-gray-700  dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 resize-none disabled:opacity-50 disabled:cursor-not-allowed border-0 focus:border-0 focus:outline-none",
                  "transition-opacity duration-500 min-h-[60px]",
                  "scrollbar-light dark:scrollbar"
                )}
                style={{
                  maxHeight: isDraggableModalOpen ? "200px" : "40px",
                  overflowY: "auto",
                  opacity: isPlaceholderVisible ? 1 : 0,
                }}
                rows={1}
              />
            </div>

            <div
              className={classNames(
                "absolute right-3 top-1/2 -translate-y-1/2 transition-all duration-300 ease-in-out",
                inputValue.trim()
                  ? "opacity-100 transform translate-x-0"
                  : "opacity-0 transform translate-x-4"
              )}
            >
              <button
                onClick={handleSubmit}
                disabled={inProgress}
                className="p-3 cursor-pointer bg-teal-400 dark:bg-teal-800 dark:hover:bg-teal-900 hover:bg-teal-500 text-white rounded-full transition-all duration-200 transform hover:scale-105"
              >
                <AwesomeIcon name={faPaperPlaneTop} size="lg" className="text-white" />
              </button>
            </div>
          </div>
          <div className="flex items-center px-5 py-3 bg-gray-600 dark:bg-[#1f2630] cursor-pointer border-t border-gray-200 dark:border-gray-700 rounded-b-2xl">
            {isTranscribing && (
              <button
                type="button"
                aria-label="Clear text"
                className="flex items-center px-4 py-2 cursor-pointer group rounded-full text-gray-400 hover:text-[#FF7869] hover:bg-[#FF7869]/10 transition-all duration-200"
                onClick={() => {
                  // Track clear button
                  trackEvent(MIXPANEL_EVENTS.ASSISTANT_CHATINPUT_CLEAR);
                  setInputValue("");
                  setInterimTranscript("");
                  baseInputValueRef.current = "";
                  baseSelectionRef.current = { start: 0, end: 0 };
                  setSelection({ start: 0, end: 0 });
                }}
              >
                <AwesomeIcon name={faTimes} size="lg" />
                <span className="ml-2 text-base font-medium group-hover:opacity-100 translate-x-0 group-hover:translate-x-1 opacity-0 transition-all duration-200">
                  Clear
                </span>
              </button>
            )}
            {/* <button
              type="button"
              onClick={onClickOpenPromptsModal}
              className={classNames(
                "flex items-center pl-5 pr-3 py-2 cursor-pointer group rounded-full text-gray-400 hover:text-teal-400 hover:bg-teal-900/20 transition-all duration-200",
                "transition-all duration-300",
                isTranscribing ? "opacity-0 -translate-x-2 pointer-events-none" : "opacity-100 translate-x-0"
              )}
            >
              <AwesomeIcon
                name={faLightbulb}
                size="lg"
                className="transition-all duration-200 group-hover:rotate-12"
              />
              <span className="ml-2 text-base font-medium group-hover:opacity-100 translate-x-0 group-hover:translate-x-1 opacity-0 transition-all duration-200">
                Prompts
              </span>
            </button> */}
            <div className="flex-1"></div>
            <div className="flex items-center relative transition-all duration-300">
              <div
                className={classNames(
                  "group flex items-center relative transition-all duration-300",
                  isTranscribing ? "ml-auto" : ""
                )}
              >
                <button
                  type="button"
                  onClick={isTranscribing ? stopRecognition : handleMicClick}
                  className={classNames(
                    "group flex items-center p-2 px-3 transition-colors duration-200",
                    isTranscribing
                      ? "text-[#FF7869]"
                      : "text-gray-200 hover:text-teal-200 dark:text-gray-400 dark:hover:text-teal-600"
                  )}
                  aria-label={isTranscribing ? "Stop recording" : "Start recording"}
                  disabled={inProgress}
                >
                  <span
                    className={classNames(
                      "mr-2 text-base font-medium whitespace-nowrap bg-gray-200 dark:bg-gray-900 px-3 py-1 rounded shadow-md border z-10 transition-all duration-300",
                      "dark:text-[#FF7869] text-brand-peach border-[#FF7869]/30 dark:border-[#FF7869]/40",
                      isTranscribing
                        ? "opacity-100 translate-x-0"
                        : "opacity-0 group-hover:opacity-100 -translate-x-2 group-hover:translate-x-0"
                    )}
                  >
                    {isTranscribing ? "Recording" : "Speak"}
                  </span>
                  <AwesomeIcon name={isTranscribing ? faStop : faMicrophone} size="lg" />
                </button>
              </div>
            </div>
            <div
              className={classNames(
                "relative transition-all duration-300 ml-3",
                isTranscribing
                  ? "opacity-0 translate-x-2 pointer-events-none"
                  : "opacity-100 translate-x-0"
              )}
            >
              <button
                type="button"
                onMouseEnter={() => setShowComingSoonPopup("voice")}
                onMouseLeave={() => setShowComingSoonPopup(null)}
                className="p-2 rounded-full text-gray-200 hover:text-teal-200 dark:text-gray-400 dark:hover:text-teal-600"
              >
                <AwesomeIcon name={faWaveformLines} size="lg" />
              </button>
              <ComingSoonPopup
                feature="voice"
                isVisible={showComingSoonPopup === "voice"}
                onClose={() => setShowComingSoonPopup(null)}
              />
            </div>
          </div>
        </div>

        <div className="flex justify-center my-3">
          <div className="sm:text-md text-sm text-gray-500 dark:text-gray-400">
            AI-powered assistance • Secure and private
          </div>
        </div>
      </div>
    </form>
  );
};

export default ChatInput;
